import { of, from, EMPTY, Subject } from 'rxjs';
import { mergeMap, catchError, share } from 'rxjs/operators';

import { closeEODShipmentsFailure } from '../shipments/eod/shipments.eod.actions';
import { pusherTimeout$ } from '../pusher/pusher.timeout.observable';
import * as PusherFallbackApi from '../pusher/fallback-api';
import { LogService } from '../../services/log-service';
import { EOD_TIMEOUT_ALLOWED_RESTARTS } from './constants';
import { getCompletedManifestMessage } from './helper';
import { noop } from 'ui-components/default-props/noop';

export const createTimeout = ({
  action$,
  findMessage$,
  jobGuid,
  pusherTimeoutMs,
  isRetry,
  startCloseEODSubject,
  onCloseEODShipmentsFailure$,
  logContext,
}) => {
  const retrySubject = new Subject();
  return pusherTimeout$({
    action$,
    clearTimeout$: findMessage$,
    errorMessage$: of(closeEODShipmentsFailure({ messageGuid: jobGuid })),
    notificationFunc: noop,
    logContext,
    retryTrigger$: retrySubject,
    timeoutMs: pusherTimeoutMs,
  }).pipe(
    mergeMap(({ retryCount }) => {
      return from(PusherFallbackApi.getMessages(jobGuid)).pipe(
        catchError(() => {
          // treat an api error like we don't have a final message yet
          return of({ messages: [] });
        }),
        mergeMap((response) => {
          const completedMessage = getCompletedManifestMessage(response);
          const isInProgress = !response?.messages?.length && retryCount < EOD_TIMEOUT_ALLOWED_RESTARTS;
          const isComplete = !!completedMessage;
          const isFailed = !response?.messages?.length && retryCount >= EOD_TIMEOUT_ALLOWED_RESTARTS;
          const didJobFailForFirstTime = isFailed && !isRetry;
          if (isInProgress) {
            retrySubject.next();
            return EMPTY;
          }
          if (isComplete) {
            LogService.logInfo('Succeeded in getting EOD message', logContext);
            return of({ pusherData: completedMessage, messageGuid: jobGuid });
          }

          if (didJobFailForFirstTime) {
            LogService.logInfo('Timeout waiting for EOD pusher message. Automatically retrying...', logContext);

            startCloseEODSubject.next(true);
            return EMPTY;
          }
          // job failed for a second time or job did not finish after restarting on the second try
          LogService.logInfo('Timeout waiting for EOD pusher message (retry). Showing error to user.', logContext);
          return onCloseEODShipmentsFailure$;
        }),
      );
    }),
    share(),
  );
};
